import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { AuthContext } from '../contexts/authcontext';
import {
  TextField,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box
} from '@mui/material';

const localizer = momentLocalizer(moment);

const PHONE_REGEX = /^\+?[0-9]{10,15}$/;

const BookingPage = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [clientPhone, setClientPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchSlots();
  }, [doctorId]);

  // Pre-fill from the account, but leave it editable — a number captured at
  // signup may be out of date, and a reminder sent to a dead number is worse
  // than no reminder.
  useEffect(() => {
    if (user?.phone) setClientPhone(user.phone);
  }, [user]);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/doctors/${doctorId}/available-slots`, {
        timeout: 15000
      });

      const availableSlots = (response.data.available || []).filter(slot =>
        slot.status !== 'expired' && new Date(slot.end_time) > new Date()
      );
      const bookedSlots = response.data.booked || [];

      const formattedSlots = [
        ...availableSlots.map(slot => ({
          id: slot.id,
          title: 'Available',
          start: new Date(slot.start_time),
          end: new Date(slot.end_time),
          status: 'available'
        })),
        ...bookedSlots.map(slot => ({
          id: slot.id,
          title: 'Booked',
          start: new Date(slot.start_time),
          end: new Date(slot.end_time),
          status: 'booked'
        }))
      ];

      setSlots(formattedSlots);
      setLoading(false);
    } catch (error) {
      let errorMessage = 'Failed to fetch slots';
      if (error.response) {
        errorMessage = error.response.data?.error || 'Server error occurred';
      } else if (error.request) {
        errorMessage = 'No response received from server';
      }

      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleSelectSlot = (slot) => {
    if (slot.status === 'available') {
      setSelectedSlot(slot);
      setPhoneError('');
      setBookingModalOpen(true);
    } else {
      toast.info('This slot is already booked');
    }
  };

  const handleBooking = async () => {
    if (!user || !user.name || !user.email) {
      toast.error('User information is missing. Please log in again.');
      return;
    }

    if (!selectedSlot || !selectedSlot.id) {
      toast.error('No slot selected. Please try again.');
      return;
    }

    if (!doctorId) {
      toast.error('Doctor information is missing. Please try again.');
      return;
    }

    const trimmedPhone = clientPhone.trim().replace(/[\s-]/g, '');
    if (!PHONE_REGEX.test(trimmedPhone)) {
      setPhoneError('Enter a valid phone number (10-15 digits)');
      return;
    }
    setPhoneError('');

    setIsBooking(true);

    try {
      const response = await axios.post('/bookings/create', {
        slotId: parseInt(selectedSlot.id),
        doctorId: parseInt(doctorId),
        clientName: user.name.trim(),
        clientEmail: user.email.trim(),
        clientPhone: trimmedPhone
      }, {
        timeout: 20000
      });

      if (response.data.success) {
        toast.success('Booking confirmed');
        setSelectedSlot(null);
        setBookingModalOpen(false);
        // Send them to the page where they can see, reschedule and cancel it.
        // Previously they stayed on the calendar with only a toast, so most
        // clients never discovered /my-bookings existed at all.
        navigate('/my-bookings');
      } else {
        toast.error(response.data.error || 'Booking failed');
      }
    } catch (error) {
      let errorMessage = 'Booking failed';

      if (error.response) {
        const status = error.response.status;

        if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 409) {
          // Another client booked this slot first.
          errorMessage = error.response.data?.error
            || 'That slot was just taken. Please choose another time.';
          await fetchSlots();
          setBookingModalOpen(false);
          setSelectedSlot(null);
        } else if (status === 404) {
          errorMessage = 'Slot not found or already booked.';
          await fetchSlots();
        } else {
          errorMessage = error.response.data?.error || 'Booking failed';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'Booking failed';
      }

      toast.error(errorMessage);
    } finally {
      setIsBooking(false);
    }
  };

  const handleCancelBooking = () => {
    setSelectedSlot(null);
    setPhoneError('');
    setBookingModalOpen(false);
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.status === 'available' ? '#4CAF50' : '#9E9E9E',
      color: 'white',
      border: 'none',
      display: 'block',
      padding: '2px 5px',
      borderRadius: '3px',
      cursor: event.status === 'available' ? 'pointer' : 'not-allowed'
    }
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button onClick={fetchSlots} variant="contained" color="primary">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <ToastContainer position="top-right" />
      <Typography variant="h4" gutterBottom>Book Appointment</Typography>

      <Calendar
        localizer={localizer}
        events={slots}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={handleSelectSlot}
        selectable
        eventPropGetter={eventStyleGetter}
        style={{ height: 500 }}
        defaultView="week"
        views={['month', 'week', 'day']}
        step={15}
        timeslots={4}
      />

      <Dialog
        open={bookingModalOpen}
        onClose={handleCancelBooking}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Booking</DialogTitle>
        <DialogContent>
          {selectedSlot && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {moment(selectedSlot.start).format('MMMM D, YYYY')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Time:</strong> {moment(selectedSlot.start).format('h:mm A')} - {moment(selectedSlot.end).format('h:mm A')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {user?.name || 'N/A'}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {user?.email || 'N/A'}
              </Typography>

              <TextField
                label="Phone number for reminders"
                value={clientPhone}
                onChange={(e) => { setClientPhone(e.target.value); setPhoneError(''); }}
                error={!!phoneError}
                helperText={phoneError || 'We will send your appointment reminder here'}
                fullWidth
                margin="normal"
                type="tel"
                disabled={isBooking}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelBooking}
            color="secondary"
            disabled={isBooking}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBooking}
            color="primary"
            variant="contained"
            disabled={isBooking}
          >
            {isBooking ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Booking...
              </>
            ) : (
              'Confirm Booking'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BookingPage;